import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

const ModalCollectiveInstallations = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();
  const [referential, setReferential] = useState([]);
  const [initial, setInitial] = useState([]);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('xsrfToken');

  useEffect(() => {
    ApiDatabase.getCollectiveInstallations(
      { token },
      (data) => setReferential(data.installations ?? []),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  }, []);

  useEffect(() => {
    if (!modalData?.idCompany) return;
    ApiDatabase.getCollectiveInstallationsCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        // RG-05: checkboxes are pre-checked to match the current state.
        const codes = (data.installations ?? []).map((i) => i.code);
        setInitial(codes);
        setSelected(codes);
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  }, [modalData?.idCompany]);

  // RG-07: Confirm stays disabled until something has actually changed.
  const hasChanged = useMemo(() => {
    if (initial.length !== selected.length) return true;
    const set = new Set(initial);
    return selected.some((code) => !set.has(code));
  }, [initial, selected]);

  const toggleCode = (code) =>
    setSelected((previous) =>
      previous.includes(code)
        ? previous.filter((c) => c !== code)
        : [...previous, code]
    );

  const handleSubmit = () => {
    setIsLoading(true);
    setError(null);
    ApiDatabase.postCollectiveInstallationsCompany(
      { token, idCompany: modalData.idCompany, codes: selected },
      (data) => {
        setIsLoading(false);
        toast.success('Les installations collectives ont été enregistrées');
        setModalDataReturn({
          type: 'collective_installations_updated',
          installations: data.installations,
        });
        toggle();
      },
      (err) => {
        setIsLoading(false);
        if (err.name === 'CanceledError') return;
        // RG-11: on error the modal stays open, the selection is preserved.
        setError(err?.response?.data?.message ?? 'Une erreur est survenue, réessayez.');
      }
    );
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Installation collective
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-1 text-sm'}>
          <div className={'text-gray-500 mb-2'}>
            Les installations que vous sélectionnez ici s’appliquent à toutes
            les missions réalisées pour cette entreprise.
          </div>
          {referential.map((installation) => (
            <label
              key={installation.code}
              className={'flex items-center gap-3 py-3 border-b'}
            >
              <input
                type={'checkbox'} className="checkbox-primary"
                checked={selected.includes(installation.code)}
                disabled={isLoading}
                onChange={() => toggleCode(installation.code)}
              />
              {installation.label}
            </label>
          ))}
          {error && <div className={'text-red-500 text-xs mt-2'}>{error}</div>}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button size={'LG'} color={'SECONDARY'} onClick={toggle}>
          Annuler
        </Button>
        <Button
          size={'LG'}
          color={!hasChanged || isLoading ? 'DISABLED' : 'PRIMARY'}
          onClick={!hasChanged || isLoading ? undefined : handleSubmit}
        >
          {isLoading ? 'Confirmation ...' : 'Confirmer'}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalCollectiveInstallations;
